/**
 *
 *  CalendarCell
 *  DateTimePicker
 *
 *  Created by Vladimir Shishlyannikov on 12/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";

// Utils
import * as dates from "../../../utils/dates";
import { isDateAvailable, getMomentWithTz } from "../../../utils/date";

function clamp(date, min, max) {
  return dates.max(dates.min(date, max), min);
}

function sameWeek(dateA, dateB) {
  return dates.eq(dateA, dateB, "week");
}

const CalendarViewCell = ({
  children,
  disabled,
  focused,
  selected,
  label,
  activeId,
  availableDays,
  now,
  min,
  max,
  date,
  view,
  culture,
  region,
  onChange,
}) => {
  const handleChange = () => {
    onChange(clamp(date, min, max));
  };

  const isAvailable = () => {
    return (
      !availableDays ||
      !availableDays.length ||
      isDateAvailable(date, availableDays, culture)
    );
  };

  const isEmpty = () => {
    return !dates.inRange(date, min, max, "day") || !isAvailable();
  };

  const isEqual = (val) => {
    return val && getMomentWithTz(date, region).isSame(val, "day"); // dates.eq(date, val, "day");
  };

  const isFocused = () => {
    return !disabled && !isEmpty() && isEqual(focused);
  };

  const isNow = () => {
    return now && isEqual(now);
  };

  const isOffView = () => {
    return date && focused && view === "week"
      ? !sameWeek(date, focused)
      : dates["month"](date) !== dates["month"](focused) &&
          !(
            /*dates.gte(date, focused, 'day') && */ (
              dates.weekday(focused) !== 0
            )
          );
  };

  const isSelected = () => {
    return selected && isAvailable() && isEqual(selected);
  };

  const isDisabled = disabled || isEmpty();

  return (
    <td
      role="gridcell"
      id={isFocused() ? activeId : null}
      title={label}
      aria-label={label}
      aria-readonly={disabled}
      aria-selected={isSelected()}
      onClick={!isDisabled ? handleChange : undefined}
      className={`tbk-text-main tbk-h-5 tbk-w-5 tbk-cursor-pointer tbk-rounded-full tbk-p-0 tbk-text-center !tbk-text-lg !tbk-leading-4`}
    >
      {children &&
        React.cloneElement(children, {
          ...children.props,
          date: date,
          isNow: isNow(),
          isDisabled,
          isEmpty: isEmpty(),
          // isOffView: isOffView(),
          isFocused: isFocused(),
          isSelected: isSelected(),
        })}
    </td>
  );
};

CalendarViewCell.propTypes = {
  id: PropTypes.string,
  activeId: PropTypes.string.isRequired,
  label: PropTypes.string,
  /**
   * Current View. For 'week' view there's a bug with displaying edge dates.
   *
   * @example ['prop', ['view', 'week', 'month', ..]]
   */
  view: PropTypes.string,
  now: PropTypes.instanceOf(Date),
  date: PropTypes.instanceOf(Date),
  selected: PropTypes.instanceOf(Date),
  focused: PropTypes.instanceOf(Date),
  /**
   * Available days to show. If not present then it will be disabled.
   *
   * @example ['prop', ['availableDays', '['2018-11-03', ..]']]
   */
  availableDays: PropTypes.array,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CalendarViewCell;
